/* Minimal fallbacks for the embedded Android WebView used by the TV. */
const globalWindow = window as typeof window & {
  __displayBootErrors?: Array<Record<string, unknown>>;
};

if (typeof globalWindow.requestAnimationFrame !== "function") {
  globalWindow.requestAnimationFrame = (callback: FrameRequestCallback): number =>
    globalWindow.setTimeout(() => callback(Date.now()), 16) as unknown as number;
}

if (typeof globalWindow.cancelAnimationFrame !== "function") {
  globalWindow.cancelAnimationFrame = (handle: number): void => globalWindow.clearTimeout(handle);
}

const stringPrototype = String.prototype as String & {
  padStart?: (targetLength: number, padString?: string) => string;
  replaceAll?: (searchValue: string | RegExp, replaceValue: string) => string;
  matchAll?: (regexp: string | RegExp) => IterableIterator<RegExpMatchArray>;
};

if (!stringPrototype.padStart) {
  Object.defineProperty(String.prototype, "padStart", {
    configurable: true,
    value(targetLength: number, padString = " ") {
      const source = String(this);
      if (source.length >= targetLength || padString.length === 0) {
        return source;
      }
      const padding = padString.repeat(Math.ceil((targetLength - source.length) / padString.length));
      return padding.slice(0, targetLength - source.length) + source;
    }
  });
}

if (!stringPrototype.replaceAll) {
  Object.defineProperty(String.prototype, "replaceAll", {
    configurable: true,
    value(searchValue: string | RegExp, replaceValue: string) {
      const source = String(this);
      if (searchValue instanceof RegExp) {
        const flags = searchValue.flags.indexOf("g") >= 0 ? searchValue.flags : searchValue.flags + "g";
        return source.replace(new RegExp(searchValue.source, flags), replaceValue);
      }
      return source.split(searchValue).join(replaceValue);
    }
  });
}

if (!stringPrototype.matchAll) {
  Object.defineProperty(String.prototype, "matchAll", {
    configurable: true,
    value(regexp: string | RegExp) {
      const source = String(this);
      const pattern = regexp instanceof RegExp
        ? new RegExp(regexp.source, regexp.flags.indexOf("g") >= 0 ? regexp.flags : regexp.flags + "g")
        : new RegExp(String(regexp), "g");
      const matches: RegExpMatchArray[] = [];
      let match: RegExpExecArray | null;
      while ((match = pattern.exec(source)) !== null) {
        matches.push(match as RegExpMatchArray);
        if (match[0] === "") {
          pattern.lastIndex += 1;
        }
      }
      return matches[Symbol.iterator]();
    }
  });
}

if (typeof globalWindow.ResizeObserver !== "function") {
  class PollingResizeObserver implements ResizeObserver {
    private readonly callback: ResizeObserverCallback;
    private timer: number | null = null;
    private target: Element | null = null;

    constructor(callback: ResizeObserverCallback) {
      this.callback = callback;
    }

    observe(target: Element): void {
      this.target = target;
      this.timer = globalWindow.setInterval(() => {
        if (!this.target) {
          return;
        }
        this.callback([], this);
      }, 250);
    }

    unobserve(): void {
      this.target = null;
    }

    disconnect(): void {
      this.unobserve();
      if (this.timer !== null) {
        globalWindow.clearInterval(this.timer);
        this.timer = null;
      }
    }

    takeRecords(): ResizeObserverEntry[] {
      return [];
    }
  }

  globalWindow.ResizeObserver = PollingResizeObserver;
}
